import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SelectBoxSmall from '../../common/SelectBox/SelectBoxSmall';
import InputBoxSmall from '../../common/InputBox/InputBoxSmall';
import CloseButton from '../../common/CloseButton/CloseButton';
import { isNotText, isRequired, maxDigit } from '../../../validation';
import InputBoxLarge from '../../common/InputBox/InputBoxLarge';
import { fetchActiveCountries } from '../../../services/Localization/CountryService';
import LargeDetailBox from '../../common/DetailBox/LargeDetailBox';
import SmallDetailBox from '../../common/DetailBox/SmallDetailBox';


const TaxPopup = ({ isOpen, mode, buttonName, defaultValue, onSave, onClose, popupErrorMessage }) => {

  const [formValues, setFormValues] = useState([defaultValue]);
  const [editedData, setEditedData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    setFormValues(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    console.log("form", formValues)
  }, [formValues])

  // useEffect(() => {
  //   console.log("Edited Data", editedData)
  // }, [editedData])

  useEffect(() => {
    if (!isOpen) {
      setFormValues({});
      setFormErrors({});
      setEditedData({});
    }
  }, [isOpen]);

  useEffect(() => {
    async function loadCountries() {
      const data = await fetchActiveCountries();
      setCountries(data);
    }

    loadCountries();
  }, []);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));

    // Track edited fields only in edit mode
    if (mode === 'edit' && defaultValue[field] !== value) {
      setEditedData(prev => ({
        ...prev,
        [field]: value,
      }));
    } else if (mode === 'edit' && defaultValue[field] === value) {
      // Remove from edited data if value matches original
      setEditedData(prev => {
        const newData = { ...prev };
        delete newData[field];
        return newData;
      });
    }

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const formSchema = [
    { name: 'name', validators: [isRequired] },
    { name: 'tax_type', validators: [isRequired] },
    { name: 'tax_rate', validators: [isRequired, isNotText, maxDigit(3)] },
    { name: 'tax_scope', validators: [isRequired] },
    { name: 'country', validators: [isRequired] },
    { name: 'effective_date', validators: [isRequired] },
    { name: 'expiry_date', validators: [isRequired] },
  ];

  const validateForm = () => {
    const errors = {};
    const dataToValidate = mode === 'edit' ? editedData : formValues;
    const fieldsToValidate = mode === 'edit' ? Object.keys(editedData) : formSchema.map(f => f.name);

    fieldsToValidate.forEach(fieldName => {
      const fieldSchema = formSchema.find(f => f.name === fieldName);
      if (!fieldSchema) return;

      const value = dataToValidate[fieldName] ?? '';

      for (const validator of fieldSchema.validators || []) {
        const result = validator(value);

        if (result === false || (typeof result === 'string' && result.length > 0)) {
          errors[fieldName] = typeof result === 'string' ? result : `${fieldName} is invalid`;
          break; // Stop at first error
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    // Send only edited data in edit mode, full form data in add mode
    const dataToSend = mode === 'edit' ? editedData : formValues;
    onSave(dataToSend);
  };

  const handleClose = () => {
    setFormValues([]);
    setFormErrors({});
    setEditedData({});
    if (onClose) onClose();
  };
         
  return (
    <div className="popup-overlay">
      <div className="popup add-popup popup1">
        <CloseButton onClick={handleClose}  />
        {popupErrorMessage && (
          <div className="popup-error-message" style={{ color: 'red', marginBottom: '10px' }}>
            {popupErrorMessage}
          </div>
        )}
        <div className="popup-name-button">
          <h2 className="popup-name fts-17 common-text ftw-5 black">
            {mode === 'detail' ? 'Tax Details' : mode === 'edit' ? 'Edit Tax' : 'Add Tax'}
          </h2>
          {mode !== 'detail' && (
              <button
                className="popup-add-button common-text white fts-15 ftw-5"
                onClick={handleSubmit}
              >
                {buttonName}
              </button>
          )}
        </div>
        <div className="add-input-box-container ai-center">
          {mode === 'detail' ? (
            <LargeDetailBox label="Tax Name" value={formValues?.name} />
          ) : (
            <InputBoxLarge
              label="Tax Name"
              validators={isRequired}
              defaultValue={formValues?.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={formErrors.name}
            />
          )}
        
          {mode === 'detail' ? (
            <SmallDetailBox label="Tax Type" value={formValues?.tax_type} />
          ) : (
            <div className="field-container w-50">
              <SelectBoxSmall
                label="Tax Type"
                options={[
                  { label: 'Fixed Amount', value: 'fixed_amount' },
                  { label: 'Percentage(%)', value: 'percentage' }
                ]}
                validators={isRequired}
                defaultValue={formValues?.tax_type}
                error={formErrors.tax_type}
                onChange={(e) => handleInputChange("tax_type", e.target.value)}
              />
            </div>
          )}
          {mode === 'detail' ? (
            <SmallDetailBox label="Tax Rate" value={formValues?.tax_rate} />
          ) : (
            <div className="field-container w-50">
              <InputBoxSmall
                label="Tax Rate"
                validators={[isRequired, isNotText, maxDigit(3)]}
                defaultValue={formValues?.tax_rate}
                error={formErrors.tax_rate}
                onChange={(e) => handleInputChange("tax_rate", e.target.value)}
              />
            </div>
          )}
          {mode === 'detail' ? (
            <SmallDetailBox label="Tax Scope" value={formValues?.tax_scope} />
          ) : (
            <div className="field-container w-50">
              <SelectBoxSmall
                label="Tax Scope"
                options={[
                  { label: 'Purchase', value: 'purchase' },
                  { label: 'Sales', value: 'sales' }
                ]}
                validators={isRequired}
                defaultValue={formValues?.tax_scope}
                error={formErrors.tax_scope}
                onChange={(e) => handleInputChange("tax_scope", e.target.value)}
              />
            </div>
          )}
          {mode === 'detail' ? (
            <SmallDetailBox label="Country" value={formValues?.country?.name} />
          ) : (
            <div className="field-container w-50">
              <SelectBoxSmall
                label="Country"
                options={countries}
                validators={isRequired}
                defaultValue={formValues?.country?.id}
                error={formErrors.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
              />
            </div>
          )}
          {mode === 'detail' ? (
            <SmallDetailBox label="Effective Date" value={formValues?.effective_date} />
          ) : (
            <div className="field-container w-50">
              <InputBoxSmall
                label="Effective Date"
                type="small-date"
                validators={isRequired}
                defaultValue={formValues?.effective_date}
                error={formErrors.effective_date}
                onChange={(e) => handleInputChange("effective_date", e.target.value)}
              />
            </div>
          )}
          {mode === 'detail' ? (
            <SmallDetailBox label="Expiry Date" value={formValues?.expiry_date} />
          ) : (
            <div className="field-container w-50">
              <InputBoxSmall
                label="Expiry Date"
                type="small-date"
                validators={isRequired}
                startDate={formValues?.effective_date}
                defaultValue={formValues?.expiry_date}
                error={formErrors.expiry_date}
                onChange={(e) => handleInputChange("expiry_date", e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TaxPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  buttonName: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TaxPopup;